import { convertMeasurement, editableMeasurement, parseMeasurementText, type MeasurementUnit } from './measurement'

/** Immutable editing state. value/baseline always use storageUnit, never displayUnit. */
export interface MeasurementDraft {
  text: string
  storageUnit: MeasurementUnit
  displayUnit: MeasurementUnit
  locale: string
  baseline: number | null
  value: number | null
  dirty: boolean
  status: 'valid' | 'empty' | 'incomplete' | 'invalid'
  reason?: string
}

export function createMeasurementDraft(value: number | null, storageUnit: MeasurementUnit, displayUnit: MeasurementUnit, locale = 'fr-FR'): MeasurementDraft {
  convertMeasurement(0, storageUnit, displayUnit)
  return { text: editableMeasurement(value, storageUnit, displayUnit, locale), storageUnit, displayUnit, locale,
    baseline: value, value, dirty: false, status: value == null ? 'empty' : 'valid' }
}

export function editMeasurementDraft(draft: MeasurementDraft, text: string): MeasurementDraft {
  if (text === draft.text) return draft
  // Undoing an edit restores the original source number, not its rounded editing text.
  if (text === editableMeasurement(draft.baseline, draft.storageUnit, draft.displayUnit, draft.locale)) {
    return { ...draft, text, value: draft.baseline, dirty: false,
      status: draft.baseline == null ? 'empty' : 'valid', reason: undefined }
  }
  const parsed = parseMeasurementText(text, draft.locale)
  const next = { ...draft, text, dirty: true, status: parsed.status, reason: 'reason' in parsed ? parsed.reason : undefined }
  if (parsed.status === 'empty') return { ...next, value: null }
  if (parsed.status !== 'valid') return next
  try {
    return { ...next, value: convertMeasurement(parsed.value, draft.displayUnit, draft.storageUnit) }
  } catch {
    return { ...next, status: 'invalid', reason: 'La mesure dépasse la plage numérique.' }
  }
}

/** Invalid/incomplete text stays attached to its original unit and locale. */
export function changeMeasurementDraftUnit(draft: MeasurementDraft, unit: MeasurementUnit, locale = draft.locale): MeasurementDraft {
  convertMeasurement(0, draft.storageUnit, unit)
  if (draft.status === 'invalid' || draft.status === 'incomplete') return draft
  if (unit === draft.displayUnit && locale === draft.locale) return draft
  return { ...draft, displayUnit: unit, locale, text: editableMeasurement(draft.value, draft.storageUnit, unit, locale) }
}

export interface MeasurementLimits { nullable?: boolean; min?: number; max?: number }
export type MeasurementCommit = { ok: true; value: number | null; changed: boolean } | { ok: false; error: string }

export function commitMeasurementDraft(draft: MeasurementDraft, limits: MeasurementLimits = {}): MeasurementCommit {
  if (draft.status === 'invalid' || draft.status === 'incomplete') return { ok: false, error: draft.reason ?? 'Terminez la saisie du nombre.' }
  const value = draft.dirty ? draft.value : draft.baseline
  if (value == null && limits.nullable === false) return { ok: false, error: 'Une valeur est requise.' }
  if (value != null && limits.min != null && value < limits.min) return { ok: false, error: 'La valeur est inférieure au minimum autorisé.' }
  if (value != null && limits.max != null && value > limits.max) return { ok: false, error: 'La valeur dépasse le maximum autorisé.' }
  return { ok: true, value, changed: value !== draft.baseline }
}
